import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Text, Image, Input, Spinner, Divider } from '@chakra-ui/react';
import { ChevronRightIcon, TriangleDownIcon } from '@chakra-ui/icons';
import { LuListTree, LuParentheses, LuSparkles } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { isAiBudgetRefusal, limitRefusal } from '../../../utils/aiUsage';
import { buildValueSuggestions, matchFields, mergeServerSuggestions } from '../../../utils/filterSuggestions';
import { translateColumnDisplayName, translateOperatorLabel } from './filterDisplayHelpers';
import { getFilterIcon } from './filterIcons';
import { renderFilterPanel } from './filterPanels';

/**
 * The body of the filter menu: value-first search.
 *
 * Typing matches the VALUES of every field at once and renders flat
 * "<Field> > <Value>" rows with the number of movements behind each, the way
 * Linear does. The field list survives as the tail of the menu, because a
 * numeric or date column has no values to match against.
 *
 * It is a plain Box rather than a Chakra MenuList on purpose: Chakra's menu
 * intercepts ArrowUp/ArrowDown/Escape/Tab before a listbox inside it can see
 * them, which is what made the old menu reachable by mouse only.
 */

// Linear's measures: 28px rows 3px apart, inset 6px from the edges with
// rounded corners; a narrow menu at rest that widens once "Field › Value"
// results need room.
const ROW_HEIGHT = '28px';
const PANEL_WIDTH = 240;
// Measured on Linear's own menu, inside its 1px border.
const MENU_WIDTH = '203px';
const MENU_WIDTH_RESULTS = '344px';

const SECTION = {
  VALUE: 'value', FIELD: 'field', AI: 'ai', ADVANCED: 'advanced', ADD_GROUP: 'addGroup',
};

const Row = React.forwardRef(({ isHighlighted, onSelect, onHover, id, children, ...rest }, ref) => (
  <Flex
    ref={ref}
    id={id}
    role="option"
    aria-selected={isHighlighted}
    align="center"
    gap="10px"
    h={ROW_HEIGHT}
    mx="6px"
    mb="3px"
    px="8px"
    borderRadius="6px"
    cursor="pointer"
    bg={isHighlighted ? 'redesign.bg.hover' : 'transparent'}
    onMouseEnter={onHover}
    onClick={onSelect}
    {...rest}
  >
    {children}
  </Flex>
));

/** "Category > Coffee     75 movements" */
const ValueRow = ({ row, isHighlighted, onSelect, onHover, id }) => {
  const { t } = useTranslation('filters');
  const FieldIcon = getFilterIcon(row);
  const isText = row.condition?.operator === 'ilike';
  return (
    <Row id={id} isHighlighted={isHighlighted} onSelect={onSelect} onHover={onHover}>
      {row.iconUrl ? (
        <Image src={row.iconUrl} alt="" boxSize="14px" objectFit="contain" borderRadius="sm" flexShrink={0}
          fallback={<Box as={FieldIcon} boxSize="14px" color="redesign.text.muted" flexShrink={0} />} />
      ) : row.icon ? (
        <Text fontSize="13px" lineHeight={1} flexShrink={0}>{row.icon}</Text>
      ) : (
        <Box as={FieldIcon} boxSize="14px" color="redesign.text.muted" flexShrink={0} />
      )}

      <Flex align="center" gap={1} minW={0} flex={1}>
        <Text fontSize="13px" color="redesign.text.muted" whiteSpace="nowrap" flexShrink={0}>
          {row.fieldLabel}
        </Text>
        {isText ? (
          // A free-text row is not a value you pick off a list, so it reads as
          // the condition it will become: Note contains "sam".
          <Text fontSize="13px" color="redesign.text.muted" whiteSpace="nowrap" flexShrink={0}>
            {translateOperatorLabel(row.condition.operator)}
          </Text>
        ) : (
          <ChevronRightIcon boxSize={3} color="redesign.text.faint" flexShrink={0} />
        )}
        <Text fontSize="13px" color="redesign.heading" isTruncated>
          {isText ? `"${row.valueLabel}"` : row.valueLabel}
        </Text>
      </Flex>

      {row.count != null && (
        <Text fontSize="12px" color="redesign.text.muted" whiteSpace="nowrap" flexShrink={0}>
          {t('menu.resultCount', { count: row.count })}
        </Text>
      )}
    </Row>
  );
};

const FieldRow = ({ filter, isHighlighted, onSelect, onHover, id }) => {
  const FieldIcon = getFilterIcon(filter);
  return (
    <Row id={id} isHighlighted={isHighlighted} onSelect={onSelect} onHover={onHover}>
      <Box as={FieldIcon} boxSize="14px" color="redesign.text.muted" flexShrink={0} />
      <Text fontSize="13px" color="redesign.text" flex={1} isTruncated>
        {translateColumnDisplayName(filter.entity, filter.column, filter.displayName)}
      </Text>
      {/* Linear's submenu mark: a small filled caret, not a chevron. */}
      <TriangleDownIcon boxSize="6px" transform="rotate(-90deg)" color="redesign.text.faint" flexShrink={0} />
    </Row>
  );
};

/** An action row at the head of the menu: icon and label, nothing after. */
const ActionRow = ({ icon, label, isHighlighted, onSelect, onHover, id }) => (
  <Row id={id} isHighlighted={isHighlighted} onSelect={onSelect} onHover={onHover}>
    <Box as={icon} boxSize="14px" color="redesign.text.muted" flexShrink={0} />
    <Text fontSize="13px" color="redesign.text" flex={1} isTruncated>{label}</Text>
  </Row>
);

const FilterValueSearch = ({
  searchRef,
  filterOptions = [],
  filterOptionsLoading = false,
  selectedFilters = [],
  onAddFilter,
  onAppendFilter,
  onUpdateFilter,
  onRemoveFilter,
  onApplySuggestion,
  timePresets = [],
  onClose,
  onOpenAdvancedPicker,
  onEnableAdvancedMode,
  isAdvancedMode = false,
  onRemoveLastFilter,
  // Injected so this component stays free of network concerns: it is handed a
  // function that resolves to { suggestions: [...] } for a query.
  fetchSuggestions,
  onAiFilter,
  onAddFilterGroup,
}) => {
  const { t } = useTranslation('filters');
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  // The field whose panel is open beside the menu. Hovering a field row is
  // enough — Linear opens it on hover, and so did this menu before.
  const [openField, setOpenField] = useState(null);
  const openFieldRef = useRef(null);
  const [panelTop, setPanelTop] = useState(0);
  const [panelFlipped, setPanelFlipped] = useState(false);
  // How far the panel moves up so a field low in the menu still opens on screen.
  const [panelLift, setPanelLift] = useState(0);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);
  const containerRef = useRef(null);
  // Adding a chip grows the toolbar, which shifts this menu down: a row slides
  // under a cursor that never moved and fires mouseenter. Without a guard,
  // ticking a value silently swapped the open panel for another field's.
  const reflowUntilRef = useRef(0);
  // Which chip the open field panel is filling in. Null until the first value
  // is ticked, because a panel opened by a passing cursor must not add a chip.
  const draftIndexRef = useRef(null);
  const [serverRows, setServerRows] = useState([]);
  const [countsLoading, setCountsLoading] = useState(false);
  const [aiState, setAiState] = useState('idle'); // idle | pending | failed
  // A refusal over a limit says which one (MI-354); any other failure the menu's own words.
  const [aiFailure, setAiFailure] = useState(null);
  const listRef = useRef(null);
  const ownInputRef = useRef(null);
  const inputRef = searchRef || ownInputRef;

  // After Chakra's own focus pass, not before it: the popover moves focus when
  // it opens, so claiming the caret on the same tick loses the race.
  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [inputRef]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /**
   * Open a field's panel alongside the row that asked for it. The panel is a
   * sibling inside this popover rather than a portal, so focus never leaves
   * the search box and the parent popover has no reason to close.
   */
  const openFieldPanel = useCallback((filter, rowEl, { fromPointer = false } = {}) => {
    // Right after a tick the menu is still settling, so a hover here is the
    // layout moving under the cursor, not the reader choosing another field.
    if (fromPointer && Date.now() < reflowUntilRef.current) return;
    clearTimeout(closeTimer.current);
    if (filter !== openFieldRef.current) draftIndexRef.current = null;
    openFieldRef.current = filter;
    setOpenField(filter);
    if (rowEl && containerRef.current) {
      const row = rowEl.getBoundingClientRect();
      const box = containerRef.current.getBoundingClientRect();
      setPanelTop(Math.max(0, row.top - box.top));
      // Flip to the left when there is no room on the right, the way a
      // submenu is expected to behave near the edge of the window.
      setPanelFlipped(box.right + PANEL_WIDTH + 8 > window.innerWidth);
    }
  }, []);

  /**
   * The panel reached from this menu always starts a NEW chip: a field that
   * already has one gets a second, which AND together. Ticking more values
   * afterwards edits that same chip rather than stacking one per tick.
   */
  const panelAddFilter = useCallback((condition) => {
    reflowUntilRef.current = Date.now() + 400;
    const index = draftIndexRef.current;
    if (!condition?.values?.length) {
      if (index != null && onRemoveFilter) {
        onRemoveFilter(index);
        draftIndexRef.current = null;
      }
      return;
    }
    if (index == null) {
      draftIndexRef.current = selectedFilters.length;
      (onAppendFilter || onAddFilter)?.(condition);
      return;
    }
    if (onUpdateFilter) {
      onUpdateFilter(index, { values: condition.values, operator: condition.operator });
    } else {
      onAddFilter?.(condition);
    }
  }, [selectedFilters.length, onAppendFilter, onAddFilter, onUpdateFilter, onRemoveFilter]);

  // A submenu that would run past the bottom of the window rises to fit, the
  // way a native submenu does; it never rises above the window's top.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!openField || !el) { setPanelLift(0); return; }
    const rect = el.getBoundingClientRect();
    const bottom = rect.bottom + panelLift;
    const overflow = bottom - (window.innerHeight - 12);
    const lift = overflow > 0 ? Math.min(overflow, rect.top + panelLift - 12) : 0;
    if (lift !== panelLift) setPanelLift(Math.max(0, lift));
  }, [openField, panelTop, panelLift]);

  const scheduleClosePanel = useCallback(() => {
    clearTimeout(closeTimer.current);
    // A short grace period: the cursor has to cross a gap to reach the panel.
    closeTimer.current = setTimeout(() => setOpenField(null), 120);
  }, []);

  const closePanelNow = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpenField(null);
  }, []);

  // Local matches render on the keystroke; the server pass only adds counts and
  // rows the client cannot know about, so the list never waits on the network.
  const localRows = useMemo(
    () => buildValueSuggestions(filterOptions, query),
    [filterOptions, query]
  );

  const valueRows = useMemo(
    () => (serverRows.length ? mergeServerSuggestions(localRows, serverRows) : localRows),
    [localRows, serverRows]
  );

  const fieldRows = useMemo(() => matchFields(filterOptions, query), [filterOptions, query]);

  useEffect(() => {
    setServerRows([]);
    if (!fetchSuggestions || !query.trim()) { setCountsLoading(false); return undefined; }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCountsLoading(true);
      try {
        const data = await fetchSuggestions(query.trim(), { signal: controller.signal });
        setServerRows(data?.suggestions || []);
      } catch (err) {
        // A superseded keystroke aborts this request — not an error worth
        // showing. Anything else just leaves the rows without their counts.
        if (!controller.signal.aborted) setServerRows([]);
      } finally {
        if (!controller.signal.aborted) setCountsLoading(false);
      }
    }, 150);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, fetchSuggestions]);

  // One flat list is what the arrow keys walk, so every row type has to live in
  // the same array in render order.
  // At rest the menu opens on its actions, as Linear's does: a new group in the
  // advanced panel, the advanced filter in the toolbar menu. Typing replaces
  // them with results.
  const actionRows = useMemo(() => {
    if (query.trim()) return [];
    const rows = [];
    if (onAddFilterGroup) rows.push({ kind: SECTION.ADD_GROUP });
    if (!isAdvancedMode && onEnableAdvancedMode) rows.push({ kind: SECTION.ADVANCED });
    return rows;
  }, [query, onAddFilterGroup, isAdvancedMode, onEnableAdvancedMode]);

  const navigable = useMemo(() => {
    const rows = [...actionRows];
    valueRows.forEach(row => rows.push({ kind: SECTION.VALUE, row }));
    fieldRows.forEach(filter => rows.push({ kind: SECTION.FIELD, filter }));
    if (query.trim() && onAiFilter) rows.push({ kind: SECTION.AI });
    return rows;
  }, [actionRows, valueRows, fieldRows, query, onAiFilter]);

  useEffect(() => { setHighlight(0); }, [query]);

  const applyValue = useCallback((row) => {
    // onApplySuggestion knows how to merge a second value into the chip the
    // first one created; without it the row still works, it just replaces.
    if (onApplySuggestion) onApplySuggestion(row);
    else onAddFilter?.(row.condition);
    onClose?.();
  }, [onApplySuggestion, onAddFilter, onClose]);

  const runAiFilter = useCallback(async () => {
    if (!onAiFilter || !query.trim()) return;
    setAiState('pending');
    setAiFailure(null);
    try {
      const ok = await onAiFilter(query.trim());
      setAiState(ok === false ? 'failed' : 'idle');
      if (ok !== false) onClose?.();
    } catch (err) {
      setAiFailure(err);
      setAiState('failed');
    }
  }, [onAiFilter, query, onClose]);

  const activate = useCallback((entry) => {
    if (!entry) return;
    if (entry.kind === SECTION.VALUE) return applyValue(entry.row);
    if (entry.kind === SECTION.FIELD) {
      const rowEl = listRef.current?.querySelector(`#filter-option-${highlight}`);
      return openFieldPanel(entry.filter, rowEl);
    }
    if (entry.kind === SECTION.AI) return runAiFilter();
    if (entry.kind === SECTION.ADVANCED) { onEnableAdvancedMode?.(); onClose?.(); }
    if (entry.kind === SECTION.ADD_GROUP) { onAddFilterGroup?.(); onClose?.(); }
    return undefined;
  }, [applyValue, runAiFilter, onEnableAdvancedMode, onAddFilterGroup, onClose, openFieldPanel, highlight]);

  const handleKeyDown = useCallback((event) => {
    if (openField && event.key === 'Escape') {
      event.preventDefault();
      closePanelNow();
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlight(i => (navigable.length ? (i + 1) % navigable.length : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlight(i => (navigable.length ? (i - 1 + navigable.length) % navigable.length : 0));
        break;
      case 'Enter':
        event.preventDefault();
        activate(navigable[highlight]);
        break;
      case 'Escape':
        event.preventDefault();
        if (query) setQuery(''); else onClose?.();
        break;
      case 'Backspace':
        // Backspace on an empty box takes back the last chip, the way it does
        // in Linear.
        if (!query && onRemoveLastFilter && selectedFilters.length) {
          event.preventDefault();
          onRemoveLastFilter();
        }
        break;
      default:
        break;
    }
  }, [openField, closePanelNow, navigable, highlight, activate, query, onClose,
      onRemoveLastFilter, selectedFilters.length]);

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    const el = listRef.current?.querySelector(`#filter-option-${highlight}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  let index = -1;

  return (
    <Box
      ref={containerRef}
      w={query.trim() && valueRows.length ? MENU_WIDTH_RESULTS : MENU_WIDTH}
      pb="6px"
      position="relative"
      onKeyDown={handleKeyDown}
    >
      <Flex align="center" gap={2} px="14px" h="38px">
        <Input
          ref={inputRef}
          variant="unstyled"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('menu.addPlaceholder')}
          h="36px"
          fontSize="13px"
          color="redesign.text"
          _placeholder={{ color: 'redesign.text.muted' }}
          aria-label={t('menu.addAria')}
          aria-controls="filter-suggestion-list"
          aria-activedescendant={navigable.length ? `filter-option-${highlight}` : undefined}
        />
        {(countsLoading || filterOptionsLoading) && <Spinner size="xs" color="redesign.text.faint" flexShrink={0} />}
      </Flex>
      <Divider />

      {/* Linear shows the whole list; it only scrolls on a short window, and
          then without a standing scrollbar. */}
      <Box
        ref={listRef}
        id="filter-suggestion-list"
        role="listbox"
        aria-label={t('menu.addAria')}
        maxH="min(560px, calc(100vh - 180px))"
        overflowY="auto"
        overflowX="hidden"
        pt="6px"
        sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {actionRows.length > 0 && (
          <>
            {actionRows.map((entry) => {
              index += 1;
              const i = index;
              const isGroup = entry.kind === SECTION.ADD_GROUP;
              return (
                <ActionRow
                  key={entry.kind}
                  id={`filter-option-${i}`}
                  icon={isGroup ? LuParentheses : LuListTree}
                  label={isGroup ? t('menu.addFilterGroup') : t('menu.advancedFilter')}
                  isHighlighted={highlight === i}
                  onHover={() => { setHighlight(i); scheduleClosePanel(); }}
                  onSelect={() => activate(entry)}
                />
              );
            })}
            {(valueRows.length > 0 || fieldRows.length > 0) && <Divider my="6px" />}
          </>
        )}

        {valueRows.map((row) => {
          index += 1;
          const i = index;
          return (
            <ValueRow
              key={row.id}
              id={`filter-option-${i}`}
              row={row}
              isHighlighted={highlight === i}
              onHover={() => { setHighlight(i); scheduleClosePanel(); }}
              onSelect={() => applyValue(row)}
            />
          );
        })}

        {fieldRows.length > 0 && (
          <>
            {valueRows.length > 0 && <Divider my="6px" />}
            {fieldRows.map((filter) => {
              index += 1;
              const i = index;
              return (
                <FieldRow
                  key={`${filter.entity}.${filter.column}`}
                  id={`filter-option-${i}`}
                  filter={filter}
                  isHighlighted={highlight === i || openField === filter}
                  onHover={(e) => { setHighlight(i); openFieldPanel(filter, e.currentTarget, { fromPointer: true }); }}
                  onSelect={(e) => openFieldPanel(filter, e.currentTarget)}
                />
              );
            })}
          </>
        )}

        {!valueRows.length && !fieldRows.length && (
          <Box px="14px" py={2}>
            <Text fontSize="13px" color="redesign.text.muted">
              {filterOptionsLoading ? t('menu.loading') : t('menu.noMatch', { query })}
            </Text>
          </Box>
        )}

        {query.trim() && onAiFilter && (() => {
          index += 1;
          const i = index;
          return (
            <>
              <Divider my="6px" />
              <Row
                id={`filter-option-${i}`}
                isHighlighted={highlight === i}
                onHover={() => { setHighlight(i); scheduleClosePanel(); }}
                onSelect={runAiFilter}
              >
                {aiState === 'pending'
                  ? <Spinner size="xs" color="redesign.text.muted" flexShrink={0} />
                  : <Box as={LuSparkles} boxSize="14px" color="redesign.text.muted" flexShrink={0} />}
                <Text fontSize="13px" color="redesign.text" flexShrink={0}>
                  {aiState === 'pending' ? t('menu.aiFilterPending') : t('menu.aiFilter')}
                </Text>
                <Text fontSize="13px" color="redesign.text.muted" isTruncated>"{query.trim()}"</Text>
              </Row>
              {aiState === 'failed' && (
                <Text px="14px" pb={1} fontSize="12px" color="redesign.accent.red">{limitRefusal(aiFailure) || t('menu.aiFilterFailed')}</Text>
              )}
              {aiState === 'failed' && isAiBudgetRefusal(aiFailure) && (
                <Text px="14px" pb={1} fontSize="12px" color="redesign.text.muted">{t('common:aiBudget.pickFilters')}</Text>
              )}
            </>
          );
        })()}
      </Box>

      {openField && (
        <Box
          ref={panelRef}
          position="absolute"
          top={`${panelTop - panelLift}px`}
          {...(panelFlipped ? { right: '100%', mr: '6px' } : { left: '100%', ml: '6px' })}
          w={`${PANEL_WIDTH}px`}
          bg="redesign.bg.popup"
          border="1px solid"
          borderColor="redesign.border"
          borderRadius="10px"
          // The theme's popup shadow, so the panel and the menu beside it
          // read as one.
          boxShadow="0 8px 28px rgba(16, 24, 40, 0.12)"
          zIndex={2}
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          // Ticking a checkbox must not pull focus out of the menu: the popover
          // closes on blur, which ended the session after one value and made
          // "several values in one chip" unreachable. The panel's own search
          // box is exempt — it needs the caret.
          onMouseDown={(e) => { if (!(e.target instanceof HTMLInputElement)) e.preventDefault(); }}
        >
          {renderFilterPanel(openField, {
            selectedFilters,
            // Scoped to the chip being built, so a field that already has a
            // chip does not open with its values pre-ticked.
            existingValues: draftIndexRef.current != null
              ? (selectedFilters[draftIndexRef.current]?.values || [])
              : [],
            onAddFilter: panelAddFilter,
            timePresets,
            searchQuery: query,
            onClose,
            onOpenAdvancedPicker,
            // The caret stays in the menu's own search box: the panel opened
            // because the pointer passed over a row, not because it was asked for.
            autoFocusSearch: false,
          })}
        </Box>
      )}

    </Box>
  );
};

export default FilterValueSearch;
